import { AxiosError, GenericAbortSignal } from 'axios'
import { atom } from 'jotai'
import { MutableRefObject } from 'react'

import { showNotification } from '@/components/Notification'
import { ROUTES } from '@/constants'
import { isAuthenticatedAtom } from '@/global-state'
import { useRouter } from '@/i18n-routing'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { getSearchParams, setSearchParam } from '@/utils'

import { captureBuilderEvent, STEP_TO_INDEX } from '../builderAnalytics'
import { qrNameAtom, TContentFormHandle } from '../contentFormSections'
import { qrUriAtom } from '../generatorState'
import { GENERATOR_SEARCH_PARAM, QR_TYPE, TQrContent } from '../generatorTypes'
import { qrDesignAtom } from '../pages/QrDesignFormPage/qrDesignState'
import { seedDraftPasswordFromTarget } from '../pages/QrDesignFormPage/qrPasswordDraft'
import { qrCodesApi } from '../services/shared'
import { getGeneratorSearchParam } from '../utils/generatorSearchParam'
import { checkValidationErrors, TSaveQrResponse } from '../utils/qrSavingUtils'
import { QR_TYPE_TO_CONTENT_ATOM } from '../utils/qrTypeToContentAtom'

export const isContentFormSubmittingAtom = atom(false)

export const createQr = async (
	qrType: QR_TYPE,
	content: TQrContent,
	abortSignal?: GenericAbortSignal,
) => {
	const postQr = getStore().get(isAuthenticatedAtom)
		? qrCodesApi.postQr
		: qrCodesApi.postGuestQr
	const {
		data: { id, uri },
	} = await postQr(
		{
			name: getStore().get(qrNameAtom) || '',
			content,
			qrDesign: null,
		},
		abortSignal,
	)
	getStore().set(qrUriAtom, uri)
	await seedDraftPasswordFromTarget(id, qrType)
	// A submit that aborted this creation has made its own draft by now
	if (abortSignal?.aborted) {
		return
	}
	setSearchParam(GENERATOR_SEARCH_PARAM.Id, id)
	captureBuilderEvent('qr_generated', {
		step_index: STEP_TO_INDEX.content,
	})
}

export const updateQr = async ({
	id,
	qrType,
}: {
	id: string
	qrType: QR_TYPE
}) => {
	const content = getStore().get(QR_TYPE_TO_CONTENT_ATOM[qrType])
	const putQr = getStore().get(isAuthenticatedAtom)
		? qrCodesApi.putQr
		: qrCodesApi.putQuestQr
	return await putQr(id, {
		name: getStore().get(qrNameAtom) || '',
		content,
		qrDesign: getStore().get(qrDesignAtom),
	})
}

export const submitContentForm = async ({
	qrType,
	router,
	resetNextButtonLoading,
	createQrOnFirstValidAbortSignalRef,
	formRefs,
	t,
}: {
	qrType: QR_TYPE
	router: ReturnType<typeof useRouter>
	resetNextButtonLoading: () => void
	createQrOnFirstValidAbortSignalRef: MutableRefObject<AbortController | null>
	formRefs: MutableRefObject<
		Record<string, { order: number; ref: TContentFormHandle }>
	>
	t: TTranslationFunction
}) => {
	try {
		getStore().set(isContentFormSubmittingAtom, true)
		createQrOnFirstValidAbortSignalRef.current?.abort()
		const qrId = getGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Id)
		if (!qrId) {
			const contentAtom = QR_TYPE_TO_CONTENT_ATOM[qrType]
			const content = getStore().get(contentAtom)
			await createQr(qrType, content)
		}
		const updatedQrId = getGeneratorSearchParam(GENERATOR_SEARCH_PARAM.Id)
		if (!updatedQrId) {
			throw new Error('Missing ID search param')
		}
		const response = await updateQr({
			id: updatedQrId,
			qrType,
		})
		checkValidationErrors(response as unknown as TSaveQrResponse)
		const currSearchParams = getSearchParams()
		router.push(`${ROUTES.generator.designStep}?${currSearchParams}`)
		router.refresh()
	} catch (error) {
		resetNextButtonLoading()
		if (
			error instanceof AxiosError &&
			error.response?.status === 400 &&
			error.response.data.message instanceof Array
		) {
			const messages = error.response?.data.message as string[]
			messages.forEach((message) => {
				const firstSpaceIndex = message.indexOf(' ')
				if (firstSpaceIndex === -1) {
					return
				}
				const property = message
					.substring(0, firstSpaceIndex)
					.replace('content.', '')
				const value = message.substring(firstSpaceIndex + 1)
				Object.values(formRefs.current).forEach((formRef) => {
					if (formRef.ref.trigger) {
						formRef.ref.trigger(property, value)
					}
				})
			})
		}
		if (error instanceof AxiosError && error.response) {
			showNotification(t('public__backend_save_error'), {
				type: 'error',
				context: 'generator',
			})
		}
		console.error(error)
	} finally {
		getStore().set(isContentFormSubmittingAtom, false)
	}
}
